import React, { SyntheticEvent, useState } from 'react';
import {
  QButton,
  QCloseButton,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QText,
  useToastProvider,
} from '@qualio/ui-components';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { CreateGroupPayload, GroupRowData, ModalComponentProps } from '../../types';
import { createErrorToast, createSuccessToast } from '../../utils/toast.utils';
import { GroupForm } from '../forms/GroupForm';
import { createGroup } from '../../api/medtech-api';

type CreateGroupModalProps = ModalComponentProps & {
  currentGroups: GroupRowData[];
  setRefetch: () => void;
};

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({ closeModal, currentGroups, setRefetch }) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const currentGroupNames = currentGroups.map((g) => g.name.toLowerCase());
  const [groupName, setGroupName] = useState('');
  const [isIncludedInReports, setIncludeInReports] = useState(true);
  const [isCreatingGroup, setCreatingGroup] = useState(false);
  const [isFormValid, setFormValid] = useState(false);

  const handleCreateGroup = async (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Return early if user pressed 'enter' when form is invalid
    if (!isFormValid) {
      return;
    }
    setCreatingGroup(true);
    const newGroup: CreateGroupPayload = {
      name: groupName.trim(),
      include_in_reports: isIncludedInReports,
    };
    try {
      await createGroup(newGroup);
      setCreatingGroup(false);
      showToast(createSuccessToast(t('messages.success.groupCreated'), 'group-create-success-toast'));
      setRefetch();
      closeModal();
    } catch (error) {
      const axiosError = error as AxiosError;
      setCreatingGroup(false);
      showToast(createErrorToast(axiosError.message || t('messages.error.createGroup'), 'group-create-error-toast'));
    }
  };

  return (
    <QModal isOpen onClose={closeModal}>
      <QModalHeader>
        <QText>{t('groups.createNewGroup')}</QText>
        <QCloseButton onClick={closeModal} />
      </QModalHeader>
      <QModalBody>
        <GroupForm
          handleSubmit={handleCreateGroup}
          currentGroupNames={currentGroupNames}
          groupName={groupName}
          setGroupName={setGroupName}
          isIncludedInReports={isIncludedInReports}
          setIncludeInReports={setIncludeInReports}
          setFormValid={setFormValid}
        />
      </QModalBody>
      <QModalActions>
        <QButton data-cy="cancel-button" variant="outline" onClick={closeModal}>
          {t('common.cancel')}
        </QButton>
        <QButton
          data-cy="confirm-button"
          isDisabled={!isFormValid || !groupName}
          isLoading={isCreatingGroup}
          onClick={handleCreateGroup}
        >
          {t('common.confirm')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
